import { UpdateUserDto } from 'api3';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { QiraUser } from '@app/services/auth.service';
import { CustomValidators } from '@app/shared/formValidation';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Button } from 'primeng/button';
import { FloatLabel } from 'primeng/floatlabel';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';

@Component({
  selector: 'app-profile-data-form',
  templateUrl: './profile-data-form.component.html',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslocoPipe, Button, FloatLabel, InputText, Select],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileDataFormComponent {
  private translocoService = inject(TranslocoService);

  get salutationOptions() {
    return [
      { value: 'mr', label: this.translocoService.translate('profile.salutation.mister') },
      { value: 'mrs', label: this.translocoService.translate('profile.salutation.mistress') },
      { value: 'na', label: this.translocoService.translate('profile.salutation.notAvailable') },
    ];
  }

  // The form is about collecting partial user data => we emit Partial<UpdateUserDto>.
  @Output() save = new EventEmitter<Partial<UpdateUserDto>>();
  @Output() dismiss = new EventEmitter<void>();

  private _user: QiraUser | undefined;
  @Input()
  get user(): QiraUser | undefined {
    return this._user;
  }
  set user(user: QiraUser | undefined) {
    this._user = user;
    this._setFormValues(user);
  }

  profileForm = new FormGroup({
    companyName: new FormControl<string>('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
    salutation: new FormControl<string>('', { nonNullable: true }),
    firstName: new FormControl<string>('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
    lastName: new FormControl<string>('', {
      validators: [Validators.required, Validators.minLength(2)],
      nonNullable: true,
    }),
    phoneNumber: new FormControl<string>('', {
      nonNullable: true,
    }),
    // email will not be submitted, this is just added to show it in the interface
    email: new FormControl<string>({ value: '', disabled: true }),
    streetPlusNumber: new FormControl<string>('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
    extraAdressEntry: new FormControl<string>('', { nonNullable: true }),
    zip: new FormControl<string>('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
    city: new FormControl<string>('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
    country: new FormControl<string>(
      { value: '', disabled: true },
      {
        nonNullable: true,
      },
    ),
    vatNumber: new FormControl<string>('', {
      validators: [CustomValidators.vat],
      nonNullable: true,
    }),
    numOfEmployees: new FormControl<number | null>(null, {
      validators: [Validators.required, Validators.min(1)],
      nonNullable: true,
    }),
    numOfPlatforms: new FormControl<number | null>(null, {
      validators: [Validators.required, Validators.min(1)],
      nonNullable: true,
    }),
    partnerCode: new FormControl<string>({ value: '', disabled: true }),
    // password will not be submitted, this is just added to show it in the interface
    password: new FormControl<string>({ value: '*******', disabled: true }),
  });

  onSubmit() {
    if (!this.profileForm.valid) {
      this.profileForm.markAllAsTouched();
      Object.values(this.profileForm.controls).forEach((control) => {
        control.markAsDirty();
      });
      return;
    }

    const formValues = this.profileForm.getRawValue();
    // Build a Partial<UpdateUserDto> for the patch
    const updateDto: Partial<UpdateUserDto> = {
      salutation: formValues.salutation,
      first_name: formValues.firstName,
      last_name: formValues.lastName,
      phone_number: formValues.phoneNumber,
      company: {
        name: formValues.companyName,
        address_line_1: formValues.streetPlusNumber,
        address_line_2: formValues.extraAdressEntry,
        zip: formValues.zip,
        city: formValues.city,
        vat_number: CustomValidators.trim(formValues.vatNumber),
        num_of_employees: formValues.numOfEmployees,
        num_of_platforms: formValues.numOfPlatforms,
        partner_code: formValues.partnerCode,
      },
    };

    this.save.emit(updateDto);
  }

  hasError(controlName: string, errorName?: string) {
    const ctrl = this.profileForm.get(controlName);
    if (!ctrl?.invalid || !ctrl.touched) return false;
    if (errorName) return ctrl.hasError(errorName);

    return true;
  }

  onCancelClick() {
    this.reset();
    this.dismiss.emit();
  }

  reset() {
    this.profileForm.reset();
    Object.keys(this.profileForm.controls).forEach((key) => {
      this.profileForm.get(key)?.setErrors(null);
    });
    this._setFormValues(this._user);
  }

  private _setFormValues(user: QiraUser | undefined) {
    if (!user) {
      this.profileForm.reset();
      return;
    }
    this.profileForm.patchValue({
      companyName: user.company.name ?? '',
      salutation: user.profile.salutation ?? '',
      firstName: user.profile.first_name ?? '',
      lastName: user.profile.last_name ?? '',
      phoneNumber: user.profile.phone_number ?? '',
      email: user.profile.email ?? '',
      streetPlusNumber: user.company?.address_line_1 ?? '',
      extraAdressEntry: user.company?.address_line_2 ?? '',
      zip: user.company?.zip ?? '',
      city: user.company?.city ?? '',
      country: this._getCountry(user),
      vatNumber: user.company?.vat_number ?? '',
      numOfEmployees: user.company?.num_of_employees ?? null,
      numOfPlatforms: user.company?.num_of_platforms ?? null,
      partnerCode: user.company?.partner_code ?? null,
    });
  }

  private _getCountry(user: QiraUser): string {
    const country: string = user.company?.country_code ?? '';
    if (!country) return country;
    return this.translocoService.translate('countryCode.' + country);
  }
}
